/**
 * Hook for managing interactive iframe communication
 * Handles scroll and resize events between parent and child frames
 */
import { useCallback, useEffect, useState } from 'react';

/**
 * Custom hook for interactive iframe functionality
 * Manages communication between parent and child frames for scroll and resize events
 * 
 * @param {Object} options - Hook options
 * @param {string} options.key - Unique key for the iframe instance
 * @param {Object} options.location - Location information to send to parent
 * @returns {Object} Object containing parentScroll, parentHeight, and maskedHeight values
 */
const useInteractiveIframe = ({ key = 'unchanged', location }) => {
  // Throttled state management using custom throttle implementation
  const [parentScroll, setParentScroll] = useState(0);
  const [parentHeight, setParentHeight] = useState(100);
  const [maskedHeight, setMaskedHeight] = useState(0);
  const [messageOrigin, setMessageOrigin] = useState();

  // Apply throttling to state setters with 200ms delay
  const throttledSetParentScroll = useCallback((value) => {
    setParentScroll(value);
  }, []);

  const throttledSetParentHeight = useCallback((value) => {
    setParentHeight(value);
  }, []);

  const throttledSetMaskedHeight = useCallback((value) => {
    setMaskedHeight(value);
  }, []);

  /**
   * Handle messages received from parent frame
   * @param {MessageEvent} event - Message event from parent
   */
  const handleParentMessage = useCallback(
    (event) => {
      if (event.source !== globalThis.parent) {
        // Keep only message from parent frame
        return;
      }

      if (!event.data) {
        if (process.env.NODE_ENV === 'development') {
          // eslint-disable-next-line no-console
          console.warn('No payload in received message!');
        }
        return;
      }

      const { data: payload } = event;

      switch (payload.type) {
        case 'scrollEvent': {
          setMessageOrigin(event.origin);
          const valueAsNumber = Number(payload.value);
          if (!Number.isNaN(valueAsNumber)) {
            throttledSetParentScroll(valueAsNumber);
          }
          break;
        }
        case 'resizeEvent': {
          setMessageOrigin(event.origin);
          const vhAsNumber = Number(payload.vh);
          if (!Number.isNaN(vhAsNumber)) {
            throttledSetParentHeight(vhAsNumber);
          }

          const maskedHeightAsNumber = Number(payload.maskedHeight);
          if (!Number.isNaN(maskedHeightAsNumber)) {
            throttledSetMaskedHeight(maskedHeightAsNumber);
          }
          break;
        }
        default: {
          if (process.env.NODE_ENV === 'development') {
            // eslint-disable-next-line no-console
            // console.log('message received by frame:', payload || event);
          }
          break;
        }
      }
    },
    [throttledSetParentScroll, throttledSetParentHeight, throttledSetMaskedHeight]
  );

  // Subscribe to parent frame messages
  useEffect(() => {
    globalThis.addEventListener('message', handleParentMessage);
    return () => globalThis.removeEventListener('message', handleParentMessage);
  }, [handleParentMessage]);

  // Send height updates to parent frame
  const postHeight = useCallback(() => {
    if (globalThis.parent && messageOrigin) {
      globalThis.parent.postMessage(
        {
          type: 'update',
          height: Math.ceil(globalThis.document.documentElement.getBoundingClientRect().height),
        },
        messageOrigin
      );
    }
  }, [messageOrigin]);

  useEffect(() => {
    postHeight();

    const interval = setInterval(postHeight, 2000);
    globalThis.addEventListener('resize', postHeight);

    return () => {
      globalThis.removeEventListener('resize', postHeight);
      clearInterval(interval);
    };
  }, [key, postHeight]);

  // Send location updates to parent frame
  const postLocation = useCallback(() => {
    if (globalThis.parent && messageOrigin) {
      globalThis.parent.postMessage(
        { type: 'update', key, location },
        messageOrigin
      );
    }
  }, [key, location, messageOrigin]);

  useEffect(() => {
    postLocation();
  }, [key, location, postLocation]);

  return {
    parentScroll,
    parentHeight,
    maskedHeight,
  };
};

export default useInteractiveIframe;
